import { createEffect, createSignal, on, Show } from 'solid-js'
import { openFile, useReadme } from '../store/view.ts'
import { cn } from '../utils/cn.ts'
import { Button } from './ui/Button.tsx'

/*
  code search collapses a readme taller than 180px and puts a "show more" under it,
  measured after render rather than guessed from the source length. ref: Mnc() in its
  bundle, which does exactly this against scrollHeight
*/
const COLLAPSED_HEIGHT = 180

export function ReadmePanel(props: { repo: string, path: string }) {
  const readme = useReadme(() => props.repo, () => props.path)
  const [collapsed, setCollapsed] = createSignal(false)
  let body: HTMLDivElement | undefined

  createEffect(on(() => readme.data?.path, () => {
    queueMicrotask(() => setCollapsed((body?.scrollHeight ?? 0) > COLLAPSED_HEIGHT))
  }))

  return (
    <Show when={readme.data}>
      {file => (
        <div class="border-b border-border">
          <div class="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-1">
            <div class="i-lucide-book-open size-3.5 shrink-0 text-muted-foreground" />
            <button
              type="button"
              class="truncate text-xs text-muted-foreground font-mono hover:text-foreground"
              onClick={() => openFile(props.repo, file().path)}
            >
              {file().name}
            </button>
          </div>

          <div class="relative">
            <div
              ref={it => (body = it)}
              class={cn('overflow-hidden px-4 py-3', collapsed() && 'max-h-45')}
            >
              <Show
                when={file().html}
                fallback={<pre class="overflow-x-auto text-xs font-mono">{file().content}</pre>}
              >
                {/* eslint-disable-next-line solid/no-innerhtml -- sanitized server-side, see services/markdown.ts */}
                <div class="markdown mx-auto max-w-3xl" innerHTML={file().html!} />
              </Show>
            </div>

            <Show when={collapsed()}>
              <div class="pointer-events-none absolute inset-x-0 bottom-0 h-12 from-background to-transparent bg-gradient-to-t" />
            </Show>
          </div>

          <Show when={collapsed()}>
            <div class="flex justify-center pb-2">
              <Button variant="outline" size="sm" class="h-6 text-xs" onClick={() => setCollapsed(false)}>
                show more
              </Button>
            </div>
          </Show>
        </div>
      )}
    </Show>
  )
}
